import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { chromeGeometry } from '@/lib/shapes/selection-chrome-preview'
import { selectionChromeFrameOfShape } from '@/lib/shapes/shape-coordinates'
import { TextOverflowBadge } from '@/components/app/editor/selection-chrome/text-overflow-badge'

export type TextOverflowBadgeLayerProps = {
  /**
   * Shapes that should show the overflow badge right now — already filtered by the caller
   * (`hooks/use-polled-canvas.ts`) to those reporting overflow at all. There is no selection or
   * editing gate: overflow is only ever reported for a frame with no next link, so every shape
   * arriving here is holding text that nothing else will draw.
   */
  shapes: ShapeData[]
  /** Current canvas zoom. */
  scale: number
}

/**
 * The text-overflow "+"/"overflow" indicator, drawn on the visual layer in screen pixels — the
 * same move `SelectionChromeLayer` made for resize anchors and `TextLinkingLayer` made for link
 * ports. Previously `ShapeTextEditor` rendered `TextOverflowBadge` itself, inline, inside the
 * canvas's own `transform: scale()` wrapper, anchored to the shape's *geometric* rect
 * (`shape.x/y/width/height`) — wrong for an inset shape (triangle, star, custom path), where the
 * geometric rect and the visible border disagree, and un-zoom-stable for the same reason the link
 * ports were before their own move.
 *
 * Uses `selectionChromeFrameOfShape` — the exact same visual-box-plus-geometric-pivot pair
 * `SelectionChromeLayer` frames resize anchors with — so the badge hugs the shape's real outline
 * and rotates about its true center, matching chrome instead of standing off an inset shape's
 * padding. This wrapper supplies the ambient `rotate(rotationDeg)` container both of
 * `TextOverflowBadge`'s pieces inherit (the same technique `SelectionChromeBadgeOverlay` uses for
 * `LockedSelectionBadge`) — this layer decides *position*, projected from a geometric doc rect to
 * screen space; `TextOverflowBadge` decides layout within that rotated box.
 *
 * A welcome side effect: `TextLinkingLayer`'s unlinked out-port "+" is meant to coincide exactly
 * with this badge's own "+" tile at every angle (see `OutPortPlusGlyph`,
 * `text-linking/components/out-port-plus-glyph.tsx`, and this component's sibling
 * `text-overflow-badge.tsx`). That guarantee could only hold at 100% zoom
 * while one side was screen space and the other was doc space; both are screen space now, so it
 * holds at every zoom again.
 *
 * HTML, not SVG — same reasoning as `SelectionChromeBadgeOverlay`: `TextOverflowBadge` is built
 * from plain positioned `<div>`s and needs a normal layout box, not a `<foreignObject>`.
 */
export function TextOverflowBadgeLayer({ shapes, scale }: TextOverflowBadgeLayerProps) {
  if (shapes.length === 0) return null

  return (
    <>
      {shapes.map((shape) => {
        const { rect, pivot, rotationDeg } = chromeGeometry(selectionChromeFrameOfShape(shape), {
          scale,
        })

        return (
          <div
            key={shape.id}
            className="absolute"
            style={{
              left: rect.x,
              top: rect.y,
              width: rect.width,
              height: rect.height,
              transform: rotationDeg ? `rotate(${rotationDeg}deg)` : undefined,
              transformOrigin: `${pivot.x - rect.x}px ${pivot.y - rect.y}px`,
              pointerEvents: 'none',
            }}
          >
            <TextOverflowBadge />
          </div>
        )
      })}
    </>
  )
}

export default TextOverflowBadgeLayer
